import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridRowsProp, GridColDef, GridCellEditStopParams, GridRenderCellParams, GridToolbar } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { API_ROUTES } from 'src/constants/routes';
import { httpClient } from 'src/libs';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useSearchParams } from "react-router-dom";
import Masquerade from 'src/components/Masquerade/Masquerade';
import { useColorMode } from 'src/theme/ThemeProvider';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useSnackbar } from 'notistack';

export function useSearchParamsState(
  searchParamName: string,
  defaultValue: any
): readonly [
  searchParamsState: string,
  setSearchParamsState: (newState: string) => void
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const acquiredSearchParam = searchParams.get(searchParamName);
  const searchParamsState = acquiredSearchParam ?? defaultValue;

  const setSearchParamsState = (newState: string) => {
    const next = Object.assign(
      {},
      [...searchParams.entries()].reduce(
        (o, [key, value]) => ({ ...o, [key]: value }),
        {}
      ),
      { [searchParamName]: newState }
    );
    setSearchParams(next);
  };
  return [searchParamsState, setSearchParamsState];
}

const UserManager = ({ user }) => {
  const [users, setUsers] = useState(null);
  const [masqueradeOpen, setMasqueradeOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  // Define all possible columns
  const allColumns: GridColDef[] = [
    { field: 'photo', headerName: '', width: 60, renderCell: renderAvatar },
    { field: 'first_name', headerName: 'First Name', width: 150 },
    { field: 'last_name', headerName: 'Last Name', width: 150, editable: true },
    { field: 'email', headerName: 'Email', width: 150 },
    { field: 'account_status', headerName: 'Account Status', width: 100, editable: true, type: "singleSelect", valueOptions: ["Default", "Active", "Rejected"] },
    { field: 'contact_phone', headerName: 'Contact Phone', width: 150 },
    { field: 'created_at', headerName: 'Created At', width: 150 },
    { field: 'updated_at', headerName: 'Last Updated', width: 150 },
    { field: 'timezone', headerName: 'Timezone', width: 150 },
    { field: 'address', headerName: 'Address', width: 250, renderCell: renderAddress },
    ...(user?.role === 11 ? [{ field: 'masquerade', headerName: 'Masquerade', width: 150, renderCell: renderMasquerade }] : []),
  ];

  // Define default visible columns
  const defaultVisibleColumns = [
    'photo',
    'first_name',
    'last_name',
    'email',
    'account_status',
    'contact_phone',
    'created_at',
    'updated_at',
    ...(user?.role === 11 ? ['masquerade'] : [])
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    // Initialize with default visible columns
    const model = {};
    allColumns.forEach(column => {
      model[column.field] = defaultVisibleColumns.includes(column.field);
    });
    return model;
  });

  const [serviceSlugs, setServiceSlugs] = useSearchParamsState("service_slugs", '');
  const [address, setAddress] = useSearchParamsState("address", '');
  const [availableSlugs, setSlugOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const dispatch = useAppDispatch();
  const { mode } = useColorMode();
  const rows: GridRowsProp = users || [];

  function renderAvatar(params: GridRenderCellParams) {
    return <img className="avatar--round" src={`${params.value}`}></img>;
  }
  function renderAddress(params: GridRenderCellParams) {
    return params.value?.formatted_address
  }
  function renderMasquerade(params: GridRenderCellParams) {
    return (
      <a
        onClick={() => handleMasqueradeClick(params.row.uuid, params.row.first_name + ' ' + params.row.last_name)}
        style={{ color: 'green', cursor: 'pointer' }}
      >
        Masquerade
      </a>
    );
  }


  function setUserData(data: any) {
    // Convert dates to timestamps for comparison
    const sortedUsers = data.users.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setUsers(sortedUsers);
    setSlugOptions(data.filters.slugs);
  }

  const fetchUsers = () => {
    setLoading(true);
    const url = new URL(`${process.env.REACT_APP_API_V3_URL}/private/users`);
    if (address) {
      url.searchParams.append("address", address);
    }
    if (serviceSlugs) {
      url.searchParams.append("service_slugs", serviceSlugs);
    }

    httpClient.get(url.href, {
      headers: {
        'Content-Type': 'application/json'
      },
    }).then(({ data }) => {
      setLoading(false);
      setUserData(data);
    })
      .catch(error => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [address, serviceSlugs]);

  const userUpdater = (payload) => {
    const url = `${API_ROUTES.bulk_user_update}`
    return httpClient.post(url, { ids: [payload.uuid], user: payload })
      .then(({ data }) => {
        if (!data.success) {
          throw new Error(data.message)
        }
      })
  }

  const handleMasqueradeClick = (userId, userName) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setMasqueradeOpen(true);
  };

  const handleMasqueradeClose = () => {
    setMasqueradeOpen(false);
    setSelectedUserId('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', overflowY: 'scroll', p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Manage Users</Typography>
        <Tooltip title="Customize Columns">
          <IconButton
            onClick={() => {
              enqueueSnackbar('Use the column selector in the toolbar to customize visible columns', {
                variant: 'info',
                autoHideDuration: 3000,
              });
            }}
          >
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          loading={loading}
          onRowClick={(params, event) => {
            event.stopPropagation();
          }}
          rows={rows}
          columns={allColumns}
          getRowId={(row) => row.uuid}
          processRowUpdate={(newRow: GridCellEditStopParams) => {
            userUpdater(newRow);
            return newRow;
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            '& .MuiDataGrid-cell': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            },
          }}
        />
      </Box>
      <Masquerade open={masqueradeOpen} onClose={handleMasqueradeClose} userId={selectedUserId} userName={selectedUserName} />
    </Box>
  );
};

export default UserManager;
